import React, { useEffect, useRef, useState } from 'react';
import { Upload, Link as LinkIcon, ImageIcon, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import PG_IMAGES from '@/constants/publicImages';

/**
 * Reusable image picker for the admin Content Studio.
 * Supports: (1) upload from device, (2) paste external URL, (3) pick from gallery
 * (curated brand presets + media library uploads).
 *
 * Props:
 *   value    — current image url
 *   onChange — (newUrl) => void
 *   label    — field label
 *   section  — optional CMS section hint stored with the upload
 *   aspect   — css aspect ratio for preview (default '3/4' for cards; use '16/9' etc.)
 */
export default function ImagePicker({ value, onChange, label, section, aspect = '3/4', testId }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('gallery');
  const [urlDraft, setUrlDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [library, setLibrary] = useState([]);
  const [loadingLib, setLoadingLib] = useState(false);
  const fileRef = useRef(null);

  const loadLibrary = async () => {
    setLoadingLib(true);
    try {
      const { data } = await api.get('/admin/media');
      setLibrary(data?.items || []);
    } catch (err) {
      // Silently ignore — gallery still shows curated presets
    } finally {
      setLoadingLib(false);
    }
  };

  useEffect(() => {
    if (open) loadLibrary();
    if (open) setUrlDraft(value || '');
  }, [open, value]);

  const readAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error('Image is too large (max 4MB)'); return; }
    setUploading(true);
    try {
      const data_url = await readAsDataURL(file);
      const { data } = await api.post('/admin/media', {
        name: file.name,
        data_url,
        section: section || '',
      });
      onChange?.(data.url);
      toast.success('Image uploaded');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const applyUrl = () => {
    const trimmed = (urlDraft || '').trim();
    if (!trimmed) { toast.error('Please paste an image URL'); return; }
    onChange?.(trimmed);
    setOpen(false);
  };

  const removeMedia = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/admin/media/${id}`);
      setLibrary((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid={testId}>
      {label && <div className="text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">{label}</div>}
      <div className="relative rounded-xl border border-white/10 bg-[#141414] overflow-hidden">
        <div className="relative w-full bg-black/30" style={{ aspectRatio: aspect }}>
          {value ? (
            <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs">
              <ImageIcon className="w-6 h-6 mr-2" /> No image
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 p-2 border-t border-white/10">
          <div className="text-[10px] text-white/50 truncate max-w-[70%]" title={value}>{value || 'No image'}</div>
          <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold hover:bg-[var(--pg-admin-accent-hover)]">
            <Upload className="w-3.5 h-3.5" /> Change
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-[#141414] border border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="text-sm font-semibold text-white">Select image</div>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-1 px-4 pt-3 border-b border-white/10 pb-3">
              {[
                { key: 'gallery', label: 'Gallery', icon: ImageIcon },
                { key: 'upload', label: 'Upload', icon: Upload },
                { key: 'url', label: 'URL', icon: LinkIcon },
              ].map((t) => (
                <button key={t.key} onClick={() => setMode(t.key)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${mode === t.key ? 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold' : 'text-white/60 hover:text-white'}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>
            <div className="p-4 overflow-y-auto">
              {mode === 'gallery' && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Brand Presets</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
                    {Object.entries(PG_IMAGES).map(([k, url]) => (
                      <button key={k} type="button" onClick={() => { onChange?.(url); setOpen(false); }} className={`relative aspect-square rounded-lg overflow-hidden ring-1 hover:ring-2 hover:ring-[var(--pg-admin-accent)] ${value === url ? 'ring-[var(--pg-admin-accent)]' : 'ring-white/10'}`}>
                        <img src={url} alt={k} className="absolute inset-0 w-full h-full object-cover" />
                        {value === url && <span className="absolute top-1.5 right-1.5 inline-flex w-6 h-6 items-center justify-center rounded-full bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]"><Check className="w-3.5 h-3.5" /></span>}
                        <span className="absolute inset-x-0 bottom-0 px-2 py-1 text-[10px] uppercase tracking-widest text-white bg-black/50">{k}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Uploaded Library</div>
                    {loadingLib && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" />}
                  </div>
                  {library.length === 0 ? (
                    <div className="mt-2 text-xs text-white/40">No uploads yet. Use the Upload tab to add your own.</div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
                      {library.map((m) => (
                        <button key={m.id} type="button" onClick={() => { onChange?.(m.url); setOpen(false); }} className={`relative aspect-square rounded-lg overflow-hidden ring-1 hover:ring-2 hover:ring-[var(--pg-admin-accent)] group ${value === m.url ? 'ring-[var(--pg-admin-accent)]' : 'ring-white/10'}`}>
                          <img src={m.url} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />
                          <span onClick={(e) => removeMedia(m.id, e)} className="absolute top-1.5 right-1.5 inline-flex w-6 h-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600"><X className="w-3.5 h-3.5" /></span>
                          {value === m.url && <span className="absolute bottom-1.5 right-1.5 inline-flex w-6 h-6 items-center justify-center rounded-full bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]"><Check className="w-3.5 h-3.5" /></span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {mode === 'upload' && (
                <div>
                  <p className="text-xs text-white/60 mb-3">Upload a PNG, JPG, WebP, GIF or SVG (max 4MB). Uploads are stored securely and reusable across all sections.</p>
                  <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold disabled:opacity-60">
                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Choose file</>}
                  </button>
                </div>
              )}
              {mode === 'url' && (
                <div>
                  <p className="text-xs text-white/60 mb-3">Paste an image URL from any source (e.g. Unsplash, your CDN).</p>
                  <input type="url" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} placeholder="https://…" className="w-full border border-white/10 bg-black/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[var(--pg-admin-accent)]" />
                  {urlDraft && <div className="mt-3 rounded-lg overflow-hidden border border-white/10 max-w-xs aspect-video bg-black/30"><img src={urlDraft} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} /></div>}
                  <div className="mt-3"><button type="button" onClick={applyUrl} className="px-4 py-2 rounded-xl bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold text-sm">Use this URL</button></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
